/**
 * Thin fetch wrapper for the Worker API. Same-origin, so the session cookie rides
 * along on its own and there is no base URL to configure per environment.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  cache?: RequestCache
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, signal, cache } = options

  const init: RequestInit = { method, credentials: 'same-origin' }
  if (signal) init.signal = signal
  if (cache) init.cache = cache
  if (body instanceof FormData) {
    // The browser sets the multipart Content-Type, boundary included.
    init.body = body
  } else if (body !== undefined) {
    init.body = JSON.stringify(body)
    init.headers = { 'Content-Type': 'application/json' }
  }

  let response: Response
  try {
    response = await fetch(`/api${path}`, init)
  } catch (cause) {
    // Offline, DNS failure, request aborted — never a status code.
    throw new ApiError(0, cause instanceof Error ? cause.message : 'Network error')
  }

  if (response.status === 204) return undefined as T

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${response.status})`
    throw new ApiError(response.status, message)
  }

  return payload as T
}
